/**
 * useTurnLifecycle — máquina STOP compartida (#494, DEC-6).
 *
 * Extrae la máquina STOP duplicada byte-a-byte entre Chat.jsx y
 * useModularChat.ts, con la semántica NUEVA (UI honesta):
 *
 *   - requestStop(): POST /stop. El backend materializa el corte EN el propio
 *     request (finalize inmediato) y responde 200 {turnId, state:'cancelled'}
 *     o 409 NO_ACTIVE_TURN (sin turno vivo → unlock sin RETRY).
 *   - La CONFIRMACIÓN real es el evento WS `stream-cancelled` (finalized:true),
 *     que el consumidor entrega vía confirmCancelled(payload). Solo entonces
 *     la UI afirma el corte (hasStopped=true) y se desbloquea (onUnlock).
 *   - Correlación TOLERANTE (crítico): el frame WS sale del servidor ANTES que
 *     la respuesta HTTP del POST y viaja por otra conexión — llegará
 *     típicamente con el turnId local aún null. Un filtro estricto por turnId
 *     descartaría la confirmación el 100 % de las veces. Regla: se acepta por
 *     threadId + stop-en-vuelo; el turnId se verifica A POSTERIORI al resolver
 *     el POST (log si difiere, nunca filtro de entrada).
 *   - Degradación honesta: si en 1,5 s no llega confirmación (pérdida de WS
 *     real), se desbloquea igualmente con hasStopped=true y un log — la UI no
 *     se queda colgada, pero el camino normal es la confirmación real.
 *   - #519: si el turno cortado llevaba un plan, un SEGUNDO evento
 *     (`stream-cancelled-plan`) trae el contador de pasos desde
 *     `plan_execution`. Ese contador YA NO VIVE AQUÍ (#898): cuelga del mensaje
 *     que abrió el turno (`message.stoppedPlan`), sellado en BD por el backend
 *     y parcheado en vivo con `useMessages.applyStoppedPlan`. Atarlo a
 *     `hasStopped` lo hacía desaparecer al pulsar RETRY, justo cuando más falta
 *     hace: el reintento apila una respuesta nueva y sin la marca el hilo deja
 *     de mencionar los pasos que ya se ejecutaron con efectos reales.
 *
 * El guard cross-op contra un RETRY en curso queda en el consumidor (envuelve
 * requestStop), porque el estado de retry vive allí.
 */

import { useState, useRef, useCallback, useEffect, type MutableRefObject } from 'react';
import threadService from '../services/threadService';

/** Payload del evento terminal `stream-cancelled` (contrato §4.4 del doc #494). */
export interface StreamCancelledPayload {
  threadId: string;
  turnId: string | null;
  reason: 'user' | 'timeout' | 'error';
  finalized: boolean;
  messageId: string | null;
  partialText: string | null;
  /** #1558: el nombre y el color con que el backend persistió el parcial (null sin parcial). */
  agentName?: string | null;
  color?: string | null;
}

/**
 * Payload del follow-up `stream-cancelled-plan` (#519): el corte llevaba un plan
 * en curso y la auditoría ya lo marcó `stopped`. Llega DESPUÉS de
 * `stream-cancelled` (la escritura en `plan_execution` no puede bloquear el
 * corte) y puede no llegar nunca — un turno sin plan no lo emite.
 *
 * Lo consume el chat, no este hook (#898): con `triggerMessageId` ancla el
 * contador al mensaje que abrió el turno, en vez de a la cola del hilo.
 */
export interface StreamCancelledPlanPayload {
  threadId: string;
  turnId: string | null;
  executionId: string;
  stepsCompleted: number;
  stepCount: number | null;
  /** uuid del mensaje del usuario que abrió el turno (ya sin sufijo `::retry-`). */
  triggerMessageId: string | null;
}

export interface UseTurnLifecycleOptions {
  threadId: string | null;
  /** Desbloquea la UI del consumidor (processingStatus, agentStatus…). */
  onUnlock?: () => void;
  /** Error de red/servidor al pedir el stop (para snackbars del consumidor). */
  onStopError?: (err: unknown) => void;
}

export interface UseTurnLifecycleReturn {
  /** true entre el click y la confirmación (o su degradación). */
  isStopping: boolean;
  /** true cuando el corte está confirmado → el consumidor puede ofrecer RETRY. */
  hasStopped: boolean;
  /** Espejo síncrono de isStopping para closures (patrón Chat.jsx). */
  isStoppingRef: MutableRefObject<boolean>;
  requestStop: () => Promise<void>;
  /**
   * Entregar aquí el payload de `stream-cancelled`. Devuelve true si el evento
   * confirmó el stop en vuelo de este hilo.
   */
  confirmCancelled: (payload: Partial<StreamCancelledPayload> | null | undefined) => boolean;
  /** Invalida todo el estado STOP (mensaje nuevo / retry / cambio de contexto). */
  clearStopState: () => void;
  /** Ajuste externo de hasStopped (mensajes isError, watchdog timeout). */
  setHasStopped: (value: boolean) => void;
}

const CONFIRM_DEGRADE_MS = 1500;

export function useTurnLifecycle({
  threadId,
  onUnlock,
  onStopError,
}: UseTurnLifecycleOptions): UseTurnLifecycleReturn {
  const [isStopping, setIsStopping] = useState(false);
  /**
   * Los TRES caminos que apagan el corte son acciones explícitas del usuario:
   * clearStopState por cambio de hilo, por mensaje nuevo y por retry.
   *
   * Eran cuatro hasta #898: el consumidor llamaba a `setHasStopped(false)`
   * cuando el agente «se reactivaba». Esa cuarta vía no era una acción del
   * usuario sino una inferencia sobre una señal del motor, y el motor N8N sigue
   * vivo tras el ⏹ — su siguiente `update_agent_status` borraba el corte solo.
   * Retirada en las dos pilas: el corte solo lo apaga el usuario.
   */
  const [hasStopped, setHasStopped] = useState(false);

  // Refs espejo — guard síncrono anti-race/anti-double-click (Chat.jsx:303,382)
  const isStoppingRef = useRef(false);
  const stopRequestRef = useRef(false); // debounce del click durante el POST
  const stopInFlightRef = useRef(false); // correlación tolerante del evento WS
  const confirmedTurnIdRef = useRef<string | null>(null); // turnId del evento, para verificación a posteriori
  const degradeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Latest-value refs para callbacks (no re-crear requestStop por identidad)
  const onUnlockRef = useRef(onUnlock);
  onUnlockRef.current = onUnlock;
  const onStopErrorRef = useRef(onStopError);
  onStopErrorRef.current = onStopError;

  const clearDegradeTimer = useCallback(() => {
    if (degradeTimerRef.current) {
      clearTimeout(degradeTimerRef.current);
      degradeTimerRef.current = null;
    }
  }, []);

  /** Cierre del ciclo: confirmado (real o degradado) o abortado (409/error). */
  const settle = useCallback((opts: { stopped: boolean; unlock: boolean }) => {
    clearDegradeTimer();
    stopInFlightRef.current = false;
    isStoppingRef.current = false;
    setIsStopping(false);
    if (opts.stopped) setHasStopped(true);
    if (opts.unlock) onUnlockRef.current?.();
  }, [clearDegradeTimer, setHasStopped]);

  const requestStop = useCallback(async () => {
    if (!threadId || isStoppingRef.current) return;
    if (stopRequestRef.current) {
      console.log('[useTurnLifecycle] STOP bloqueado: solicitud ya en progreso (double-click)');
      return;
    }

    try {
      stopRequestRef.current = true;
      // El orden importa: stop-en-vuelo marcado ANTES del POST — el evento WS
      // puede llegar antes que el 200 y debe encontrar la marca.
      stopInFlightRef.current = true;
      confirmedTurnIdRef.current = null;
      isStoppingRef.current = true;
      setIsStopping(true);

      console.log(`[useTurnLifecycle] 🛑 STOP solicitado para thread ${threadId}`);

      const result = await threadService.stopExecution(threadId);
      console.log('[useTurnLifecycle] Stop 200:', result);

      // Verificación a posteriori del turnId (nunca filtro de entrada): si el
      // evento ya confirmó con otro turnId, solo se deja constancia.
      if (
        confirmedTurnIdRef.current &&
        result?.turnId &&
        confirmedTurnIdRef.current !== result.turnId
      ) {
        console.warn(`[useTurnLifecycle] turnId del evento (${confirmedTurnIdRef.current}) ≠ turnId del 200 (${result.turnId})`);
      }

      // Degradación honesta armada SOLO tras el 200: el backend ya materializó
      // el corte (state 'cancelled'), así que declarar hasStopped sin el evento
      // WS es verdad — solo falta la confirmación visual. Armarla antes del
      // POST podría afirmar un corte que aún no ocurrió (POST lento ≠ WS caído),
      // y un error tardío del POST no puede revertir un hasStopped ya declarado.
      if (stopInFlightRef.current) {
        clearDegradeTimer();
        degradeTimerRef.current = setTimeout(() => {
          if (!stopInFlightRef.current) return;
          console.warn('[useTurnLifecycle] ⚠️ 200 recibido pero sin stream-cancelled en 1,5 s (¿WS caído?) — degradación honesta: unlock + hasStopped');
          settle({ stopped: true, unlock: true });
        }, CONFIRM_DEGRADE_MS);
      }
    } catch (err: any) {
      if (err?.status === 409 && err?.code === 'NO_ACTIVE_TURN') {
        // ⏹ sin turno vivo: no hay nada que cortar — unlock SIN ofrecer RETRY.
        console.log('[useTurnLifecycle] 409 NO_ACTIVE_TURN — sin turno vivo, unlock sin RETRY');
        settle({ stopped: false, unlock: true });
      } else {
        console.error('[useTurnLifecycle] Error al pedir stop:', err);
        settle({ stopped: false, unlock: false });
        onStopErrorRef.current?.(err);
      }
    } finally {
      stopRequestRef.current = false;
    }
  }, [threadId, settle, clearDegradeTimer]);

  const confirmCancelled = useCallback((payload: Partial<StreamCancelledPayload> | null | undefined): boolean => {
    if (!payload || !threadId || payload.threadId !== threadId) return false;
    if (!stopInFlightRef.current) return false; // sin stop en vuelo: evento ajeno (p. ej. timeout del GC)
    confirmedTurnIdRef.current = payload.turnId ?? null;
    console.log(`[useTurnLifecycle] ✅ Corte confirmado por stream-cancelled (turnId=${payload.turnId ?? 'n/a'}, reason=${payload.reason ?? 'n/a'})`);
    settle({ stopped: true, unlock: true });
    return true;
  }, [threadId, settle]);

  const clearStopState = useCallback(() => {
    clearDegradeTimer();
    stopInFlightRef.current = false;
    isStoppingRef.current = false;
    // El debounce también se resetea: sin esto, un POST en vuelo del hilo
    // ANTERIOR bloquearía silenciosamente el primer ⏹ del hilo nuevo.
    stopRequestRef.current = false;
    setIsStopping(false);
    setHasStopped(false);
  }, [clearDegradeTimer, setHasStopped]);

  // Reset al cambiar de hilo + cleanup StrictMode-safe (CRA dev monta→desmonta→
  // remonta: un timer sin clearTimeout en el cleanup queda huérfano).
  useEffect(() => {
    clearStopState();
    return () => {
      if (degradeTimerRef.current) {
        clearTimeout(degradeTimerRef.current);
        degradeTimerRef.current = null;
      }
    };
  }, [threadId, clearStopState]);

  return {
    isStopping,
    hasStopped,
    isStoppingRef,
    requestStop,
    confirmCancelled,
    clearStopState,
    setHasStopped,
  };
}
